import { Button, keyframes, Tooltip, Modal, Box, Typography } from '@mui/material';
import LoopIcon from '@mui/icons-material/Loop';
import useServerDown from 'hooks/useServerDown';
import { useState } from 'react';
import { OfflineScan, syncScans } from 'lib/models/access';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbarError, enqueueSnackbarSuccess } from 'lib/helpers';
import { useNavigate } from 'react-router-dom';
import useUser from 'hooks/useUser';

const spin = keyframes({
	'100%': {
		transform: 'rotate(360deg)',
	},
});

type Props = {
	refetch?: () => Promise<void>;
};

export default function ButtonSync(props: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [openModal, setOpenModal] = useState(false);
	const isDown = useServerDown();
	const [scanQueue, setScanQueue] = useState<OfflineScan[]>(JSON.parse(localStorage.getItem('scanQueue') || '[]'));
	const [isSyncing, setIsSyncing] = useState(false);
	const [offlineSuccess, setOfflineSuccess] = useState<string[]>([]);
	const [offlineErrors, setOfflineErrors] = useState<{ code: string; message: string; type: string }[]>([]);
	const { user } = useUser();

	const isAdmin = user?.type === 'admin' || user?.type === 'parkingAdmin';

	const syncStoredScan = async () => {
		try {
			setIsSyncing(true);
			const { errors, successScans } = await syncScans();
			setOfflineErrors(errors);
			setOfflineSuccess(successScans);
			setOpenModal(true); // Abrir modal después de la sincronización

			if (!errors.length) {
				enqueueSnackbarSuccess(t(`common:successSync`));
			} else {
				enqueueSnackbarError(t(`common:failSync`));
			}

			localStorage.setItem('scanQueue', '[]');
			setScanQueue([]);
			props.refetch && props.refetch();
		} catch (error) {
			enqueueSnackbarError(t(`common:errorSync`));
		} finally {
			setIsSyncing(false);
		}
	};

	return (
		<>
			{!isDown && scanQueue.length > 0 && (
				<Tooltip title={isSyncing ? 'Sincronizando...' : 'Sincronizar'}>
					<span>
						<Button
							variant="contained"
							onClick={syncStoredScan}
							color="primary"
							sx={{ fontSize: '0.8rem', paddingX: '0.3rem', marginRight: '.2rem' }}
						>
							<LoopIcon
								sx={{
									animation: isSyncing ? `${spin} 1s linear infinite` : 'none',
								}}
							/>
							{t('access:sync')}
						</Button>
					</span>
				</Tooltip>
			)}

			{/* Modal de resumen */}
			<Modal open={openModal} onClose={() => setOpenModal(false)}>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						textAlign: 'center',
					}}
				>
					<Typography variant="h6">{t('access:sync_plate')}</Typography>
					<Typography sx={{ mt: 2 }}>
						✅ {t('common:success_mov_sync')}: <strong>{offlineSuccess.length}</strong>
					</Typography>
					<Typography sx={{ mt: 1 }}>
						❌ {t('common:error_mov_sync')}: <strong>{offlineErrors.length}</strong>
					</Typography>
					<Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
						{isAdmin && (
							<Button variant="contained" color="primary" onClick={() => navigate('/report')}>
								{t('access:viewReports')}
							</Button>
						)}
						<Button variant="outlined" onClick={() => setOpenModal(false)}>
							{t('common:close')}
						</Button>
					</Box>
				</Box>
			</Modal>
		</>
	);
}
